import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import Select from "react-select";
import { toast } from "react-toastify";
import {
  LuDatabase,
  LuFilter,
  LuLayers,
  LuListFilter,
  LuUser,
} from "react-icons/lu";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import ActivityLogList from "../../components/activityLogs/ActivityLogList";
import Pagination from "../../components/pagination";
import Button from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import {
  actionBarClass,
  cardClass,
  cardContentClass,
  cardHeaderClass,
  cardTitleClass,
  fieldIconClass,
  fieldInputClass,
  filterFooterClass,
  filterGridClass,
  filterLabelClass,
  pageTitleClass,
  summaryLabelClass,
  summaryValueClass,
} from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchActivityLogsData,
  getActivityLogs,
  getActivityLogsUsers,
  getTotalActivityLogs,
} from "../../features/activityLogs/activityLogsSlice";
import {
  getListState,
  saveListState,
} from "../../features/listState/listStateSlice";
import { activityLogModules } from "../../static/activityLog";
import { route } from "../../static/route";
import { iconSelectStyles, toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";

type OptionsProps = {
  value: string | number;
  label: string;
};

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "એક્ટિવિટી લોગ" },
];

const actionOptions: OptionsProps[] = [
  { value: "update", label: "અપડેટ" },
  { value: "delete", label: "ડિલીટ" },
];

const listKey = "activityLogs";

const ActivityLogsPage = () => {
  const dispatch = useAppDispatch();
  const logs = useAppSelector(getActivityLogs);
  const totalLogs = useAppSelector(getTotalActivityLogs);
  const users = useAppSelector(getActivityLogsUsers);
  const savedList = useAppSelector((state) => getListState(state, listKey));

  const [userOptions, setUserOptions] = useState<OptionsProps[]>([]);

  interface FilterFormInput {
    fromDate: string;
    toDate: string;
    action: string;
    model_type: string;
    user_id: number | null;
  }

  const emptyFilters: FilterFormInput = {
    fromDate: "",
    toDate: "",
    action: "",
    model_type: "",
    user_id: null,
  };

  const { handleSubmit, register, reset, getValues, control, setValue } =
    useForm<FilterFormInput>({ defaultValues: savedList.filters });

  const [page, setPage] = useState<number>(savedList.page || 1);
  const [pageSize, setPageSize] = useState<number>(savedList.pageSize || 50);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(
    !!savedList.showFilter
  );

  const fetchId = useRef(0);

  const paginate = (pageNumber: number) => {
    setPage(pageNumber);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleOnFilterSubmit = async () => {
    if (page === 1) {
      fetchLogsList(1, pageSize);
    } else {
      setPage(1);
    }
  };

  const handleClearFilter = () => {
    reset(emptyFilters);
    if (page === 1) {
      fetchLogsList(1, pageSize);
    } else {
      setPage(1);
    }
  };

  const fetchLogsList = async (page: number, size: number) => {
    const data = getValues();
    const currentFetch = ++fetchId.current;

    dispatch(saveListState({ key: listKey, value: { filters: data } }));
    setIsLoading(true);

    dispatch(
      fetchActivityLogsData({
        page: page,
        size: size,
        fromDate: data?.fromDate || null,
        toDate: data?.toDate || null,
        action: data?.action || null,
        model_type: data?.model_type || null,
        user_id: data?.user_id || null,
      })
    ).then((action: any) => {
      const result = action?.payload?.data;

      if (currentFetch === fetchId.current && result?.status !== "success") {
        toast.error(
          result?.message || "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.",
          { ...(toastStyles as any) }
        );
      }

      // A saved page that is now past the end (its rows deleted, say) moves to the last page.
      const lastPage = Math.max(
        1,
        Math.ceil((Number(result?.data?.total_records) || 0) / size)
      );

      if (
        currentFetch === fetchId.current &&
        page > 1 &&
        result?.status === "success" &&
        !result?.data?.records?.length &&
        lastPage < page
      ) {
        setPage(lastPage);
        return;
      }

      setIsLoading(false);
    });
  };

  useEffect(() => {
    const options =
      users?.map((user) => ({ value: user.id, label: user.name })) ?? [];
    setUserOptions(options);
  }, [users]);

  // Coming back to the list (from a record's page, say) shows the same page and filters.
  useEffect(() => {
    dispatch(
      saveListState({ key: listKey, value: { page, pageSize, showFilter } })
    );
  }, [dispatch, page, pageSize, showFilter]);

  useEffect(() => {
    fetchLogsList(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>એક્ટિવિટી લોગ ની વિગતો</h1>
      </div>

      <div className="space-y-4 md:space-y-6">
        <div className={actionBarClass}>
          <div className="flex items-center gap-2">
            <span className={summaryLabelClass}>ટોટલ રેકોર્ડ:</span>
            <span className={summaryValueClass}>{totalLogs}</span>
          </div>

          <div className="grid w-full grid-cols-1 gap-2 sm:flex sm:w-auto">
            <Button variant="outline" onClick={() => setShowFilter(!showFilter)}>
              <LuFilter className="h-4 w-4" />
              ફિલ્ટર
            </Button>
          </div>
        </div>

        {showFilter && (
          <div className={cardClass}>
            <div className={cardHeaderClass}>
              <div className={`${cardTitleClass} text-lg`}>ફિલ્ટર</div>
            </div>

            <div className={cardContentClass}>
              <form onSubmit={handleSubmit(handleOnFilterSubmit)}>
                <div className={filterGridClass}>
                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ થી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("fromDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>તારીખ સુધી</label>
                    <div className="relative">
                      <LuDatabase className={fieldIconClass} />
                      <Input
                        type="date"
                        className={fieldInputClass()}
                        {...register("toDate")}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>એકશન</label>
                    <div className="relative">
                      <LuListFilter className={fieldIconClass} />
                      <Controller
                        name="action"
                        control={control}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            value={
                              actionOptions.find(
                                (val) => val.value === field.value
                              ) || null
                            }
                            styles={iconSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="એકશન સિલેક્ટ કરો"
                            options={actionOptions}
                            onChange={(val: any) =>
                              setValue("action", val?.value || "")
                            }
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>મોડ્યુલ</label>
                    <div className="relative">
                      <LuLayers className={fieldIconClass} />
                      <Controller
                        name="model_type"
                        control={control}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            value={
                              activityLogModules.find(
                                (val) => val.value === field.value
                              ) || null
                            }
                            styles={iconSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="મોડ્યુલ સિલેક્ટ કરો"
                            options={activityLogModules}
                            onChange={(val: any) =>
                              setValue("model_type", val?.value || "")
                            }
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className={filterLabelClass}>યુઝર</label>
                    <div className="relative">
                      <LuUser className={fieldIconClass} />
                      <Controller
                        name="user_id"
                        control={control}
                        render={({ field }) => (
                          <Select
                            isClearable
                            {...field}
                            value={
                              userOptions.find(
                                (val) => val.value === field.value
                              ) || null
                            }
                            styles={iconSelectStyles}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            placeholder="યુઝર સિલેક્ટ કરો"
                            options={userOptions}
                            onChange={(val: any) =>
                              setValue("user_id", val?.value ?? null)
                            }
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className={filterFooterClass}>
                  <Button
                    type="button"
                    variant="outline"
                    className="max-sm:flex-1"
                    onClick={handleClearFilter}
                  >
                    ફિલ્ટર સાફ કરો
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    className="max-sm:flex-1"
                  >
                    ફિલ્ટર લાગુ કરો
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        <ActivityLogList
          logs={logs}
          pageNumber={page}
          pageSize={pageSize}
          isLoading={isLoading}
        />

        {!isLoading && logs.length > 0 && (
          <Pagination
            itemsPerPage={pageSize}
            totalItems={totalLogs}
            paginate={paginate}
            currentPage={page}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="રેકોર્ડ"
          />
        )}
      </div>
    </AppLayout>
  );
};
export default ActivityLogsPage;
